import { useEffect, useRef, useState } from "react";

// Mobile pull-to-refresh: drag down at the top of the page to reload.
export function usePullToRefresh(onRefresh, { threshold = 70, enabled = true } = {}) {
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef(null);
  const pullRef = useRef(0);
  const busyRef = useRef(false);
  const cbRef = useRef(onRefresh);
  cbRef.current = onRefresh;

  useEffect(() => {
    if (!enabled) return undefined;
    const scroller = () => document.scrollingElement || document.documentElement;
    const set = (v) => { pullRef.current = v; setPull(v); };
    const onStart = (e) => {
      startY.current = (scroller().scrollTop <= 0 && !busyRef.current) ? e.touches[0].clientY : null;
    };
    const onMove = (e) => {
      if (startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && scroller().scrollTop <= 0) set(Math.min(dy * 0.5, threshold + 30));
      else if (dy <= 0) set(0);
    };
    const onEnd = async () => {
      if (startY.current == null) return;
      startY.current = null;
      if (pullRef.current >= threshold) {
        busyRef.current = true; setRefreshing(true); set(threshold);
        try { await cbRef.current?.(); } catch { /* noop */ }
        finally { setTimeout(() => { busyRef.current = false; setRefreshing(false); set(0); }, 400); }
      } else set(0);
    };
    window.addEventListener("touchstart", onStart, { passive: true });
    window.addEventListener("touchmove", onMove, { passive: true });
    window.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      window.removeEventListener("touchstart", onStart);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    };
  }, [threshold, enabled]);

  return { pull, refreshing };
}
